import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Users, Mail, Settings, BarChart3, ArrowLeft, Lock,
  UserPlus, Trash2, Crown, Pencil, Copy, Check, X, RefreshCw,
} from 'lucide-react';
import Header from '../components/Header';
import GlowBackground from '../components/GlowBackground';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: 'overview', label: 'Overview',  icon: BarChart3 },
  { id: 'members',  label: 'Members',   icon: Users },
  { id: 'invites',  label: 'Invites',   icon: Mail },
  { id: 'settings', label: 'Settings',  icon: Settings },
];

const TeamAdmin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const orgParam = searchParams.get('org') || '';
  const orgQuery = useMemo(() => (orgParam ? `?org=${encodeURIComponent(orgParam)}` : ''), [orgParam]);

  if (authLoading) return <FullScreenLoader />;

  if (!isAuthenticated) {
    return (
      <BlockedState
        icon={Lock}
        message="Sign in to access the team admin panel."
      />
    );
  }
  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return (
      <BlockedState
        icon={Lock}
        title="Team admin access required"
        message="Ask an existing admin in your organisation to promote you."
      />
    );
  }

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
      <GlowBackground />
      <Header />
      <Toaster position="top-center" richColors />

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="team-admin-back"
            className="inline-flex items-center gap-1.5 text-sm text-charcoal/60 hover:text-charcoal transition-colors"
          >
            <ArrowLeft size={14} /> Back to home
          </button>
          <Link
            to="/directory"
            className="inline-flex items-center gap-1.5 text-sm text-orange-primary hover:underline"
          >
            <Users size={14} /> Team directory
          </Link>
        </div>

        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="font-serif text-4xl text-charcoal">Team Admin</h1>
          <p className="text-charcoal/60 mt-1">
            Manage your organisation’s members, invites, and settings.
            {orgParam && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-charcoal/5 text-charcoal/70 text-xs">
                Acting on: {orgParam}
              </span>
            )}
          </p>
        </motion.header>

        {/* Tabs */}
        <div
          className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-charcoal/5 mb-5"
          data-testid="team-admin-tabs"
        >
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm transition-colors ${
                activeTab === t.id
                  ? 'bg-white text-charcoal shadow-sm'
                  : 'text-charcoal/60 hover:text-charcoal'
              }`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <OverviewTab orgQuery={orgQuery} setActiveTab={setActiveTab} />}
        {activeTab === 'members'  && <MembersTab  orgQuery={orgQuery} currentUserId={user.id} />}
        {activeTab === 'invites'  && <InvitesTab  orgQuery={orgQuery} />}
        {activeTab === 'settings' && <SettingsTab orgQuery={orgQuery} />}
      </main>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
    <div className="w-10 h-10 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
  </div>
);

const BlockedState = ({ icon: Icon = Lock, title = 'Sign in required', message }) => (
  <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFF8F0' }}>
    <Header />
    <div className="flex-1 flex items-center justify-center px-6 text-center">
      <div>
        <Icon size={32} className="mx-auto mb-3 text-charcoal/30" />
        <h2 className="font-serif text-2xl text-charcoal mb-2">{title}</h2>
        <p className="text-charcoal/60 mb-4 max-w-sm">{message}</p>
        <Link to="/" className="text-orange-primary hover:underline text-sm">Back to home</Link>
      </div>
    </div>
  </div>
);

const Stat = ({ label, value, hint }) => (
  <div className="rounded-2xl bg-white/50 border border-charcoal/5 p-4">
    <div className="text-xs uppercase tracking-wider text-charcoal/50">{label}</div>
    <div className="font-serif text-3xl text-charcoal mt-1">{value}</div>
    {hint && <div className="text-xs text-charcoal/40 mt-1">{hint}</div>}
  </div>
);

// ── Overview ──────────────────────────────────────────────────────────────────

const OverviewTab = ({ orgQuery, setActiveTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/team/overview${orgQuery}`)
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || 'Could not load overview.'))
      .finally(() => setLoading(false));
  }, [orgQuery]);

  if (loading) return <Spinner />;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="overview-tab">
      <h2 className="font-serif text-2xl text-charcoal">{data.organisation}</h2>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Members"           value={data.member_count} />
        <Stat label="Admins"            value={data.admin_count} />
        <Stat label="Profiles complete" value={data.profiles_completed}
              hint="≥50% of profile filled" />
        <Stat label="Pending invites"   value={data.pending_invites} />
      </div>

      <section className="rounded-3xl bg-white/40 border border-charcoal/5 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-charcoal">Recently joined</h3>
          <button
            type="button"
            onClick={() => setActiveTab('members')}
            className="text-sm text-orange-primary hover:underline"
          >
            View all members →
          </button>
        </div>
        {data.recent_joins.length === 0 ? (
          <div className="text-sm text-charcoal/40 italic">No recent joins yet.</div>
        ) : (
          <ul className="divide-y divide-charcoal/5">
            {data.recent_joins.map(m => (
              <li key={m.user_id} className="py-2 flex items-center gap-3">
                <Avatar member={m} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-charcoal truncate">
                    {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.display_name || m.email}
                    {m.role === 'admin' && (
                      <span className="ml-2 text-xs text-orange-primary inline-flex items-center gap-0.5">
                        <Crown size={10} /> admin
                      </span>
                    )}
                  </div>
                  {m.position && <div className="text-xs text-charcoal/50 truncate">{m.position}</div>}
                </div>
                <div className="text-xs text-charcoal/40">{m.profile_completeness}% profile</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

// ── Members tab ───────────────────────────────────────────────────────────────

const MembersTab = ({ orgQuery, currentUserId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // user_id being edited
  const [draft, setDraft] = useState({});
  const [busyId, setBusyId] = useState(null);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/team/members${orgQuery}`)
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || 'Could not load members.'))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [orgQuery]);

  const startEdit = (m) => {
    setEditing(m.user_id);
    setDraft({
      first_name: m.first_name || '',
      last_name: m.last_name || '',
      position: m.position || '',
      role: m.role,
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const saveEdit = async (m) => {
    setBusyId(m.user_id);
    try {
      await axios.put(`${API}/team/members/${m.user_id}${orgQuery}`, draft);
      toast.success('Member updated.');
      cancelEdit();
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save changes.');
    } finally {
      setBusyId(null);
    }
  };

  const removeMember = async (m) => {
    const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    if (!window.confirm(`Remove ${fullName} from the organisation?`)) return;
    setBusyId(m.user_id);
    try {
      await axios.delete(`${API}/team/members/${m.user_id}${orgQuery}`);
      toast.success(`${fullName} removed.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove member.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Spinner />;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="members-tab">
      {data.members.map(m => {
        const isEditing = editing === m.user_id;
        const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ')
          || m.display_name || m.email;
        return (
          <div
            key={m.user_id}
            className="rounded-2xl bg-white/50 border border-charcoal/5 p-4"
            data-testid={`member-row-${m.user_id}`}
          >
            <div className="flex items-start gap-3">
              <Avatar member={m} large />
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input value={draft.first_name} onChange={v => setDraft(d => ({...d, first_name: v}))} placeholder="First name" />
                    <Input value={draft.last_name}  onChange={v => setDraft(d => ({...d, last_name: v}))}  placeholder="Last name" />
                    <Input value={draft.position}   onChange={v => setDraft(d => ({...d, position: v}))}   placeholder="Position" className="sm:col-span-2" />
                    <select
                      value={draft.role}
                      onChange={e => setDraft(d => ({ ...d, role: e.target.value }))}
                      data-testid={`role-select-${m.user_id}`}
                      className="px-3 py-2 rounded-lg border border-charcoal/10 bg-white text-sm sm:col-span-2"
                      disabled={m.is_self || m.role === 'super_admin'}
                    >
                      <option value="user">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="font-medium text-charcoal flex items-center gap-2 flex-wrap">
                      {fullName}
                      {m.is_self && <span className="text-xs px-1.5 py-0.5 rounded bg-orange-primary/10 text-orange-primary">You</span>}
                      {m.role === 'admin' && <span className="text-xs text-orange-primary inline-flex items-center gap-0.5"><Crown size={10}/> admin</span>}
                      {m.role === 'super_admin' && <span className="text-xs text-charcoal/50 inline-flex items-center gap-0.5"><Crown size={10}/> super admin</span>}
                    </div>
                    <div className="text-sm text-charcoal/60">{m.position || '—'}</div>
                    <div className="text-xs text-charcoal/40 mt-0.5">{m.email}</div>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isEditing ? (
                  <>
                    <button type="button" onClick={() => saveEdit(m)} disabled={busyId === m.user_id}
                      data-testid={`save-${m.user_id}`}
                      className="px-3 py-1.5 rounded-full bg-orange-primary text-white text-sm hover:bg-orange-primary/90 transition-colors disabled:opacity-50">
                      Save
                    </button>
                    <button type="button" onClick={cancelEdit}
                      className="p-1.5 rounded hover:bg-charcoal/5 transition-colors"
                      aria-label="Cancel">
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => startEdit(m)}
                      data-testid={`edit-${m.user_id}`}
                      className="p-2 rounded-full hover:bg-charcoal/5 transition-colors"
                      aria-label="Edit">
                      <Pencil size={14} />
                    </button>
                    {!m.is_self && m.role !== 'super_admin' && (
                      <button type="button" onClick={() => removeMember(m)} disabled={busyId === m.user_id}
                        data-testid={`remove-${m.user_id}`}
                        className="p-2 rounded-full hover:bg-red-500/10 text-red-500/70 hover:text-red-500 transition-colors disabled:opacity-50"
                        aria-label="Remove">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-charcoal/40">
              <span>{m.profile_completeness}% profile complete</span>
              {m.last_active && <span>· last active {new Date(m.last_active).toLocaleDateString()}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Invites tab ───────────────────────────────────────────────────────────────

const InvitesTab = ({ orgQuery }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [submitting, setSubmitting] = useState(false);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/team/invites${orgQuery}`)
      .then(r => setInvites(r.data || []))
      .catch(err => toast.error(err?.response?.data?.detail || 'Could not load invites.'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [orgQuery]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/team/invites${orgQuery}`, { email: email.trim(), role });
      toast.success(`Invite created — share the link with ${r.data.email}`);
      setEmail(''); setRole('user'); setShowForm(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    try {
      await axios.delete(`${API}/team/invites/${inv.token}${orgQuery}`);
      toast.success('Invite revoked.');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not revoke invite.');
    }
  };

  return (
    <div className="space-y-3" data-testid="invites-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-charcoal">Invites</h2>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)}
            data-testid="new-invite-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-primary text-white text-sm hover:bg-orange-primary/90 transition-colors">
            <UserPlus size={14} /> New invite
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit}
          className="rounded-2xl bg-white/60 border border-charcoal/5 p-4 space-y-3"
          data-testid="invite-form">
          <Input value={email} onChange={setEmail} placeholder="teammate@company.com" type="email" testId="invite-email" />
          <select value={role} onChange={e => setRole(e.target.value)}
            data-testid="invite-role"
            className="w-full px-3 py-2 rounded-lg border border-charcoal/10 bg-white text-sm">
            <option value="user">Member</option>
            <option value="admin">Admin</option>
          </select>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={submitting || !email.trim()}
              data-testid="send-invite"
              className="px-4 py-1.5 rounded-full bg-orange-primary text-white text-sm hover:bg-orange-primary/90 transition-colors disabled:opacity-50">
              {submitting ? 'Creating…' : 'Create invite'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEmail(''); }}
              className="px-4 py-1.5 rounded-full bg-charcoal/5 text-charcoal/70 text-sm hover:bg-charcoal/10 transition-colors">
              Cancel
            </button>
          </div>
          <p className="text-xs text-charcoal/50">
            We’ll generate a unique sign-up link. Copy it from the list below and send it through your own email or chat.
          </p>
        </form>
      )}

      {loading ? <Spinner /> : invites.length === 0 ? (
        <div className="text-sm text-charcoal/40 italic py-6 text-center">No invites yet.</div>
      ) : (
        <ul className="divide-y divide-charcoal/5 rounded-2xl bg-white/50 border border-charcoal/5 overflow-hidden">
          {invites.map(inv => <InviteRow key={inv.token} inv={inv} onRevoke={revoke} />)}
        </ul>
      )}
    </div>
  );
};

const InviteRow = ({ inv, onRevoke }) => {
  const [copied, setCopied] = useState(false);
  const expired = new Date(inv.expires_at) <= new Date();
  const accepted = !!inv.accepted_at;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inv.invite_url);
      setCopied(true);
      toast.success('Invite link copied.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select and copy the link manually.');
    }
  };

  let status = 'Pending', statusClass = 'text-charcoal/60';
  if (accepted) { status = 'Accepted'; statusClass = 'text-green-600'; }
  else if (expired) { status = 'Expired'; statusClass = 'text-red-500/70'; }

  return (
    <li className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`invite-row-${inv.token}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-charcoal truncate">{inv.email}</div>
        <div className="text-xs text-charcoal/50 mt-0.5">
          {inv.role === 'admin' ? 'Admin' : 'Member'} ·{' '}
          <span className={statusClass}>{status}</span>
          {!accepted && !expired && <> · expires {new Date(inv.expires_at).toLocaleDateString()}</>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!accepted && !expired && (
          <button type="button" onClick={copy}
            data-testid={`copy-invite-${inv.token}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-charcoal/5 hover:bg-charcoal/10 transition-colors">
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy link'}
          </button>
        )}
        {!accepted && (
          <button type="button" onClick={() => onRevoke(inv)}
            data-testid={`revoke-${inv.token}`}
            className="p-2 rounded-full hover:bg-red-500/10 text-red-500/70 hover:text-red-500 transition-colors"
            aria-label="Revoke invite">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </li>
  );
};

// ── Settings tab ──────────────────────────────────────────────────────────────

const SettingsTab = ({ orgQuery }) => {
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/overview${orgQuery}`)
      .then(r => { setData(r.data); setName(r.data.organisation); })
      .catch(err => toast.error(err?.response?.data?.detail || 'Could not load settings.'));
  }, [orgQuery]);

  const rename = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === data?.organisation) return;
    if (!window.confirm(`Rename your organisation from "${data?.organisation}" to "${name.trim()}"? All members and invites will be updated.`)) return;
    setSaving(true);
    try {
      await axios.put(`${API}/team/name${orgQuery}`, { new_name: name.trim() });
      toast.success('Organisation renamed. Refreshing…');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not rename.');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="settings-tab">
      <section className="rounded-3xl bg-white/40 border border-charcoal/5 p-5">
        <h3 className="font-medium text-charcoal mb-1">Organisation name</h3>
        <p className="text-sm text-charcoal/50 mb-4">
          Rename your organisation. Every teammate's profile and pending invites will move to the new name immediately.
        </p>
        <form onSubmit={rename} className="flex flex-col sm:flex-row gap-2">
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            data-testid="org-name-input"
            className="flex-1 px-3 py-2 rounded-lg border border-charcoal/10 bg-white text-sm focus:outline-none focus:border-orange-primary/50 transition-colors"
          />
          <button type="submit" disabled={saving || !name.trim() || name.trim() === data.organisation}
            data-testid="save-org-name"
            className="px-4 py-2 rounded-full bg-orange-primary text-white text-sm hover:bg-orange-primary/90 transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      </section>
    </div>
  );
};

// ── Tiny utilities ────────────────────────────────────────────────────────────

const Avatar = ({ member, large = false }) => {
  const initial = (member.first_name || member.display_name || member.email || '?').trim().charAt(0).toUpperCase();
  const size = large ? 'w-12 h-12 text-lg' : 'w-9 h-9 text-sm';
  return (
    <div className={`${size} rounded-full bg-orange-primary/10 text-orange-primary flex items-center justify-center font-serif flex-shrink-0 overflow-hidden`}>
      {member.avatar_url
        ? <img src={member.avatar_url} alt="" className="w-full h-full object-cover" />
        : initial}
    </div>
  );
};

const Spinner = () => (
  <div className="py-12 flex justify-center">
    <RefreshCw className="text-charcoal/30 animate-spin" size={20} />
  </div>
);

const Input = ({ value, onChange, placeholder, type = 'text', className = '', testId }) => (
  <input
    type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    data-testid={testId}
    className={`px-3 py-2 rounded-lg border border-charcoal/10 bg-white text-sm focus:outline-none focus:border-orange-primary/50 transition-colors ${className}`}
  />
);

export default TeamAdmin;
